import type { Container } from "pixi.js";
import type { ComputedGridLayout, GridEffect, Log, PresentationTaskRunner } from "@slot/presentation";
import type { VsWildTuning } from "../../tuning/vsWildTuning";
import type { VsBattleInput } from "./replay/ExpandingWildActionHandler";
import type { VsBattleWilds } from "./VsBattleFeature";
import { VsWildView, type VsWildLabelTarget } from "./VsWildView";

/** What the layer reads from the grid: the layout of the reel a battle covers, and the root that layout is in. */
export interface VsWildGrid {
  readonly gridColumnCount: number;
  readonly gridRowCount: number;
  readonly gridRoot: Container | null;
  getComputedLayout(): ComputedGridLayout;
}

/** A battle's top contender arriving from elsewhere (the Super Bonus saved total flies in and becomes the label). */
export interface VsWildTopLabelEntrance {
  readonly column: number;
  /** The top contender's value, without the suffix. */
  readonly topValue: string;
  /** The battle's scope: a fly run in it is slammed and speed-invariant with the battle. */
  readonly scope: string;
  /** The label's resting spot, read when the fly starts. */
  target(): VsWildLabelTarget;
}

/** A board handed back at the next spin, to scroll out with its reel. */
export interface ActiveVsWild {
  readonly node: Container;
  readonly column: number;
}

/** Each battle's runner scope: `vs_wild_0`, `vs_wild_1`… within a spin, apart from the reels' and the wins'. */
const SCOPE_PREFIX = "vs_wild";

/**
 * The VS battles of a spin over their reels (Cocos `VsSpineWildLayer`), a grid effect: each `expanding_wild` places a
 * board over its reel, fitted to it, and plays the battle to its end; the boards stay over the grid until the next
 * spin, which takes them (`takeActiveWilds`) to scroll out with their reels. Each battle runs in its own scope at 1×
 * in every speed mode (the battle is the round's moment), and a slam finishes only the battle playing
 * (`slamActiveWild`); the next one plays until slammed too. Cosmetic: the grid's cells are not touched.
 */
export class VsWildLayer implements GridEffect, VsBattleWilds {
  private readonly active: ActiveVsWild[] = [];
  private battleCount = 0;
  private activeScope: string | null = null;

  public constructor(
    private readonly input: {
      grid: VsWildGrid;
      /** Above the symbols, beside the grid's clip (`GridContainer.gridOverlay`): the labels slide in from beside the reel. */
      parent: Container;
      /** One board per battle; null when its art is missing (the battle then plays its sound and the bear only). */
      createView: () => VsWildView | null;
      runner: PresentationTaskRunner;
      tuning: VsWildTuning;
      /** Replaces the top label's slide when it resolves true. */
      topLabelEntrance?: (entrance: VsWildTopLabelEntrance) => Promise<boolean>;
      log: Log;
    },
  ) {}

  public removeSymbolsByMask(): Promise<void> {
    return Promise.resolve();
  }

  /** The boards still held (none after `takeActiveWilds`) go, and the spin's battle scopes are reset. */
  public clearForNewSpin(): void {
    for (const { node } of this.active.splice(0)) if (!node.destroyed) node.destroy({ children: true });
    for (let i = 0; i < this.battleCount; i += 1) this.input.runner.clearScope(scopeFor(i));
    this.battleCount = 0;
    this.activeScope = null;
  }

  /** Hands the boards over without destroying them: the next spin scrolls them out with their reels. */
  public takeActiveWilds(): ActiveVsWild[] {
    return this.active.splice(0);
  }

  /** Finishes the battle playing now (its board jumps to the result); false when none plays. */
  public slamActiveWild(): boolean {
    return this.activeScope ? this.input.runner.slamFinishScope(this.activeScope) : false;
  }

  /** One battle, played to its end (the engine awaits it, so a spin's battles play left to right). */
  public async expandColumn(battle: VsBattleInput): Promise<void> {
    const { grid, parent, runner, tuning } = this.input;
    const gridRoot = grid.gridRoot;
    const column = battle.positionColumn;
    if (!gridRoot || column < 0 || column >= grid.gridColumnCount) {
      this.input.log(`[VsWildLayer] no reel ${column} to place the battle on`);
      return;
    }
    const view = this.input.createView();
    if (!view) return;

    // Centred on the reel and fitted to it (the prefab's 206×958.1 board on a 206.5×950 reel).
    const layout = grid.getComputedLayout();
    const centre = { x: layout.originX + column * layout.cellWidth + (layout.columnOffsets[column] ?? 0), y: layout.originY + ((grid.gridRowCount - 1) * layout.cellHeight) / 2 };
    parent.addChild(view);
    view.position.copyFrom(parent.toLocal(centre, gridRoot));
    const { width, height } = VsWildView.authoredSize;
    view.scale.set(Math.min(layout.cellWidth / width, (grid.gridRowCount * layout.cellHeight) / height));
    this.active.push({ node: view, column });

    view.prepareHidden(tuning.startHeight);
    view.setMultipliers(`${battle.multipliers[0]}${tuning.multiplierSuffix}`, `${battle.multipliers[1]}${tuning.multiplierSuffix}`);

    const scope = scopeFor(this.battleCount);
    this.battleCount += 1;
    this.activeScope = scope;
    runner.setScopeScale(scope, 1);
    try {
      await view.playReveal({ runner, tuning, scope });
      const entrance = this.input.topLabelEntrance;
      await view.playBattle({
        winningIndex: battle.winningIndex,
        runner,
        tuning,
        scope,
        topLabelEntrance: entrance ? () => entrance({ column, topValue: battle.multipliers[0], scope, target: () => view.topLabelTarget() }) : undefined,
      });
    } finally {
      runner.clearScopeScale(scope);
      if (this.activeScope === scope) this.activeScope = null;
    }
  }
}

function scopeFor(index: number): string {
  return `${SCOPE_PREFIX}_${index}`;
}
